<template>
  <GmapMap
    id="map"
    ref="gmapRef"
    class="mc-notification-map-history"
    :options="gMapOptions"
    :zoom="zoom"
    :center="center"
  >
    <gmap-custom-marker
      v-for="(alert, index) in alerts"
      :key="index"
      :marker="{
        lat: Number(alert.latitude),
        lng: Number(alert.longitude),
      }"
      alignment="center"
      :offset-x="-3"
      :offset-y="-33"
    >
      <div v-if="index == 0" class="custom-marker">
        <div
          class="double-marker-pin"
          :class="{
            red: hasUrgentScope ? alert.isUrgent : alert.isImportant,
            yellow: hasUrgentScope ? alert.isImportant : false,
          }"
        >
          <div class="left-circle">
            <i class="mc-icon icon-fill_flag"></i>
          </div>
          <div class="right-circle">
            <img :alt="alert.alert.alertCode" :src="alert.alert.iconUrl" />
          </div>
        </div>
      </div>

      <div v-else class="custom-marker">
        <div
          class="marker-pin"
          :class="{
            red: hasUrgentScope ? alert.isUrgent : alert.isImportant,
            yellow: hasUrgentScope ? alert.isImportant : false,
          }"
        ></div>
        <div class="marker-icon">
          <img :alt="alert.alert.alertCode" :src="alert.alert.iconUrl" />
        </div>
      </div>
    </gmap-custom-marker>
  </GmapMap>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import { mapGetters } from 'vuex'
import GmapCustomMarker from 'vue2-gmap-custom-marker'

export default {
  name: 'MapHistory',

  components: {
    'gmap-custom-marker': GmapCustomMarker,
  },
  props: {
    detailData: {
      type: Object,
      required: true,
    },
    selectedAlert: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      gMapOptions: {},
      polylines: [],
      alerts: [],
      map: null,
      zoom: 15,
      center: { lat: -2.958881, lng: 109.5543666 },
    }
  },
  computed: {
    google: gmapApi,
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    hasUrgentScope() {
      return this.hasAccess(['e4e-alert-center-urgent:read'])
    },
  },

  watch: {
    selectedAlert: {
      handler() {
        this.center = {
          lat: this.selectedAlert.alert.latitude,
          lng: this.selectedAlert.alert.longitude,
        }
        this.renderMarkerAlert()
      },
      deep: true,
    },
  },

  async mounted() {
    this.reloadMap()
  },
  methods: {
    async reloadMap() {
      const self = this
      await this.$refs.gmapRef.$mapPromise.then((map) => {
        self.map = map

        self.gMapOptions = {
          minZoom: 3,
          mapTypeControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: self.google.maps.ControlPosition.LEFT_BOTTOM,
          },
          scaleControl: true,
          streetViewControl: true,
          streetViewControlOptions: {
            position: self.google.maps.ControlPosition.LEFT_BOTTOM,
          },
          fullscreenControl: false,
        }
      })

      this.drawPolyline()
      if (this.detailData.previousAlerts.length > 0) {
        this.renderMarkerAlert()
      }
    },

    drawPolyline() {
      let bounds = new this.google.maps.LatLngBounds()
      const locationHistory = this.detailData.locationHistory

      for (let i = 0; i < locationHistory.length - 1; i++) {
        const start = locationHistory[i]
        const end = locationHistory[i + 1]

        bounds.extend(new this.google.maps.LatLng(start.latitude, start.longitude))

        const path = new this.google.maps.Polyline({
          path: [
            { lat: parseFloat(start.latitude), lng: parseFloat(start.longitude) },
            { lat: parseFloat(end.latitude), lng: parseFloat(end.longitude) },
          ],
          strokeColor: this.getColorForSpeed(start.speed),
          strokeOpacity: 1.0,
          strokeWeight: 5,
        })

        path.setMap(this.map)
      }
      this.map.fitBounds(bounds)
    },

    getColorForSpeed(speed) {
      if (speed >= 0 && speed <= 20) return '#f41d00'
      if (speed > 20 && speed <= 40) return '#f64a33'
      if (speed > 40 && speed <= 60) return '#ffa133'
      if (speed > 60 && speed <= 80) return '#71b099'
      return '#137c55'
    },

    renderMarkerAlert() {
      const markers = this.detailData.previousAlerts.map((alert) => {
        return alert
      })
      this.alerts = markers

      if (this.detailData.locationHistory.length <= 1) {
        let bounds = new this.google.maps.LatLngBounds()
        markers.forEach((marker) => {
          bounds.extend(new this.google.maps.LatLng(marker.latitude, marker.longitude))
        })
        this.map.fitBounds(bounds)
      }
    },
  },
}
</script>

<style lang="scss">
.mc-notification-map-history {
  height: calc(100vh - 190px);
  width: 100%;
}

.custom-marker {
  position: absolute;
  transform-origin: center;
  top: -20px;

  .marker-pin {
    width: 40px;
    height: 40px;
    border-radius: 50% 50% 50% 50%;
    background: #fff;
    position: absolute;
    transform: rotate(-45deg);
    left: 50%;
    top: 50%;
    margin: -16px 0 0 -16px;

    &:before {
      content: '';
      width: 0;
      height: 0;
      position: absolute;
      bottom: -20px;
      left: -10%;
      transform: translateX(-50%) rotate(45deg);
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 30px solid #fff;
    }

    &:after {
      content: '';
      width: 32px;
      height: 32px;
      margin: 4px 0px 0px 4px;
      background: #808080;
      position: absolute;
      border-radius: 50%;
    }

    &.red:after {
      background: $red;
    }

    &.yellow:after {
      background: $yellow;
    }
  }

  .marker-icon {
    width: 24px;
    height: 24px;
    position: absolute;
    left: 50%;
    top: 50%;
    margin: -8px 0 0 -8px;
    z-index: 1;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: brightness(0) invert(1);
    }
  }
}

.custom-marker {
  position: absolute;
  transform-origin: center;

  .double-marker-pin {
    width: 68px;
    height: 40px;
    border-radius: 20px;
    background: #fff;
    position: absolute;
    left: -50%;
    top: 50%;
    margin: -32px 0 0 -32px;

    &:before {
      content: '';
      width: 0;
      height: 0;
      position: absolute;
      bottom: -40px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 40px solid #fff;
      z-index: 0;
    }

    .left-circle {
      width: 34px;
      height: 34px;
      margin: 3px 0 0 4px;
      background: $mep-primary;
      position: absolute;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid $white;

      i {
        color: $white;
        filter: brightness(0) invert(1);
        font-size: 24px;
      }
    }

    .right-circle {
      width: 34px;
      height: 34px;
      margin: 3px 0 0 30px;
      background: #808080;
      position: absolute;
      border-radius: 50%;
      border: 2px solid $white;

      img {
        width: 70%;
        height: 70%;
        object-fit: contain;
        position: absolute;
        left: 10%;
        top: 10%;
        filter: brightness(0) invert(1);
      }
    }

    &.red {
      .right-circle {
        background: $red;
      }
    }

    &.yellow {
      .right-circle {
        background: $yellow;
      }
    }
  }
}
</style>
